import {
  TaskSaveData,
  UserData,
  UserGroupData,
  WellData,
  WellGroupData,
} from '../../components/add-task-modal/AddTaskDataModals';
import { CategoryData, CategoryDataTier1, TaskApiResponse, TaskData } from '../../components/task-details-widget/TaskDataModels';
import api from '../api';
import { createWrapperPayload, wrapperConstants } from '../wrapper-api';

// Promise Queue (to prevent multiple simultaneous requests)
const fetchQueue = {
  wells: null as Promise<WellData[]> | null,
  users: null as Promise<UserData[]> | null,
  userGroups: null as Promise<UserGroupData[]> | null,
  wellGroups: null as Promise<WellGroupData[]> | null,
  categories: null as Promise<CategoryData[]> | null,
};

// Fetch well list
export const fetchWells = async (isPC: boolean | undefined = true) => {
  if (fetchQueue.wells) {
    return await fetchQueue.wells;
  }

  const requestPayload = createWrapperPayload('GET', '/pumpchecker/Lookup/GetWellLookups');

  if (isPC) {
    fetchQueue.wells = api.post(wrapperConstants.router, requestPayload).then((response) => {
      fetchQueue.wells = null;
      return response.data ?? [];
    });

    return fetchQueue.wells;
  } else {
    fetchQueue.wells = api.get('Comments/assets').then((response) => {
      fetchQueue.wells = null;
      return response.data.values.map((asset: { assetId: string, assetName: string }) => ({ Key: asset.assetId, Value: asset.assetName })) ?? [];
    });

    return fetchQueue.wells;

  }
};

// Fetch user list
export const fetchUsers = async () => {
  if (fetchQueue.users) {
    return await fetchQueue.users;
  }

  const requestPayload = createWrapperPayload('GET', '/pumpchecker/Lookup/GetUsers');

  fetchQueue.users = api.post(wrapperConstants.router, requestPayload).then((response) => {
    fetchQueue.users = null;
    return response.data ?? [];
  });

  return fetchQueue.users;
};

// Fetch user groups
export const fetchUserGroups = async () => {
  if (fetchQueue.userGroups) {
    return await fetchQueue.userGroups;
  }

  const requestPayload = createWrapperPayload('GET', '/pumpchecker/Lookup/GetUserGroups');

  fetchQueue.userGroups = api.post(wrapperConstants.router, requestPayload).then((response) => {
    fetchQueue.userGroups = null;
    return response.data ?? [];
  });

  return fetchQueue.userGroups;
};

// Fetch well groups
export const fetchWellGroups = async () => {
  if (fetchQueue.wellGroups) {
    return await fetchQueue.wellGroups;
  }

  const requestPayload = createWrapperPayload('GET', '/pumpchecker/Lookup/GetWellGroupsWithAnyUser');

  fetchQueue.wellGroups = api.post(wrapperConstants.router, requestPayload).then((response) => {
    fetchQueue.wellGroups = null;
    return response.data ?? [];
  });

  return fetchQueue.wellGroups;
};

// Fetch only categories from real API
export const fetchCategories = async (isPC: boolean | undefined = true): Promise<CategoryData[]> => {
  if (fetchQueue.categories) {
    return await fetchQueue.categories;
  }

  const requestPayload = createWrapperPayload('GET', '/pumpchecker/Lookup/GetCategories?type=Task');

  if (isPC) {
    fetchQueue.categories = api.post<CategoryData[]>(wrapperConstants.router, requestPayload).then((response) => {
      fetchQueue.categories = null;
      return response.data ?? [];
    });
  } else {
    fetchQueue.categories = api.get<{ values: CategoryDataTier1[] }>('/Comments/category').then((response) => {
      fetchQueue.categories = null;
      const data = response.data.values.map(item => {
        return {
          CategoryId: item.categoryId,
          Name: item.category,
          CategoryTypeEnum: item.categoryId,
        }
      })
      return data ?? [];
    });
  }

  return fetchQueue.categories;
};

// Fetch task data from real API and map categories
// This function fetches task data and categories from the API and maps the category names to the tasks
export const fetchTaskData = async (wellId: string): Promise<TaskData[]> => {
  try {
    const taskRequestPayload = createWrapperPayload('GET', `/pumpchecker/Well/GetWellTasks?wellId=${wellId}`);
    const categoryRequestPayload = createWrapperPayload('GET', '/pumpchecker/Lookup/GetCategories?type=Task');
    const [tasksResponse, categoriesResponse] = await Promise.all([
      api.post<TaskApiResponse>(wrapperConstants.router, taskRequestPayload),
      api.post<CategoryData[]>(wrapperConstants.router, categoryRequestPayload),
    ]);

    const tasksData = tasksResponse.data;
    const categories = categoriesResponse.data;

    // Map category names to tasks
    const tasksWithCategories = tasksData.Data.map((task) => {
      if (task.CategoryId) {
        const category = categories.find((cat) => cat.CategoryId === task.CategoryId);
        return {
          ...task,
          CategoryName: category ? category.Name : 'Unknown',
        };
      }
      return task;
    });

    return tasksWithCategories;
  } catch (error) {
    console.error('Error fetching task data or categories:', error);
    throw error;
  }
};

// Save task data (create or update)
export const saveTaskData = async (taskData: TaskSaveData): Promise<boolean> => {
  try {
    const formData = new URLSearchParams();

    // Add required default fields
    formData.append('sort', '');
    formData.append('group', '');
    formData.append('filter', '');

    // Set defaults for all known expected fields
    const fullPayload: { [key: string]: any } = {
      WellTaskId: '',
      CategoryId: '',
      CategoryName: '',
      UserId: '',
      AssignedTo: '',
      UserName: '',
      UserAlias: '',
      WellId: '',
      WellName: '',
      Identifier: '',
      WellGroupId: '',
      WellGroupName: '',
      UserGroupId: '',
      UserGroupName: '',
      Task: '',
      TaskNumber: '',
      Comment: '',
      CreatedBy: '',
      CurrentUserInUserGroup: 'false',
      CurrentUserIsAssigned: 'false',
      CurrentUserIsCreator: 'false',
      WellFieldAreaName: '',
      CallbackFunction: '',
      DaysBeforeStopping: '0',
      DueDate: '',
      CompletedDate: '',
      Created: new Date().toLocaleString('en-GB').replace(',', ''), // Optional: override from taskData if needed
      DueDateString: '',
      ChangeDetectionId: '',
      ChangeDetectionConfirmedPositive: '',
      ChangeDetectionEventId: '',
      ChangeDetectionConfirmQuestion: '',
      TaskType: '0',
      HasActiveTrackedChange: 'false',
      Id: '',
    };

    // Overwrite defaults with actual taskData values
    Object.entries(taskData).forEach(([key, value]) => {
      fullPayload[key] = value ?? '';
    });

    // Append to formData
    Object.entries(fullPayload).forEach(([key, value]) => {
      formData.append(key, value?.toString() ?? '');
    });

    const requestPayload = createWrapperPayload('POST', `/pumpchecker/Well/UpdateWellTask`, formData.toString(), true);

    const response = await api.post(wrapperConstants.router, requestPayload);

    return response && response.status === 200;
  } catch (error) {
    console.error('Error saving task data to API:', error);
    return false;
  }
};

export const deleteTask = async (taskData: TaskSaveData): Promise<boolean> => {
  try {
    const formData = new URLSearchParams();

    Object.entries(taskData).forEach(([key, value]) => {
      formData.append(key, value?.toString() ?? '');
    });

    formData.append('sort', '');
    formData.append('group', '');
    formData.append('filter', '');

    const requestPayload = createWrapperPayload(
      'POST',
      `/pumpchecker/Well/DeleteWellTask?wellTaskId=${taskData.WellTaskId}`,
      formData.toString(),
    );

    const response = await api.post(wrapperConstants.router, requestPayload);

    return response.status === 200;
  } catch (error) {
    console.error('Delete task failed:', error);
    return false;
  }
};

export const markAsCompleteTaskSave = async (wellTaskId: number): Promise<boolean> => {
  try {
    const formData = new URLSearchParams();
    formData.append('wellTaskId', wellTaskId.toString());

    const requestPayload = createWrapperPayload('POST', `/pumpchecker/Well/CompleteWellTask?wellTaskId=${wellTaskId}`);

    const response = await api.post(wrapperConstants.router, requestPayload);
    return response.status === 200;
  } catch (error) {
    console.error('Delete task failed:', error);
    return false;
  }
};
